import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Input, Modal, Select, Typography, message } from 'antd';
import { useNotificationContext } from '@/features/notifications';
import { useProcInstantiate } from '@/shared/api';
import { Col, Row } from '@/shared/ui';
import { emailPattern } from '@/shared/utils';
import { captureError } from '@/shared/utils/error-reporting';

type Props = {
  open: boolean;
  procGroupId: string;
  onCancel: () => void;
};

const { Title } = Typography;

export const ProcShareModal: FC<Props> = ({ open, onCancel, procGroupId }) => {
  const { showNotification } = useNotificationContext();

  const [form] = Form.useForm();
  const selectRef = useRef(null);

  const [error, setError] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  const emails = Form.useWatch('emails', form);

  const { mutate: shareProc, isLoading: shareProcLoading } =
    useProcInstantiate();

  const iFrameCode = useMemo(
    () =>
      `<iframe width="100%" height="100%" src="${
        import.meta.env.VITE_BASE_URL
      }/procs/${procGroupId}/preview" allowfullscreen></iframe>`,
    [procGroupId],
  );

  const validateEmails = (emails: string[]) => {
    setError(false);
    const invalidEmails = emails
      .map((email) => email.trim())
      .filter((email) => !emailPattern.test(email));

    if (invalidEmails.length) {
      setError(true);
    }
  };

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(
      import.meta.env.VITE_BASE_URL + `/procs/${procGroupId}/preview`,
    );
    setLinkCopied(true);
    setEmbedCopied(false);
    message.success('Link copied to clipboard');
  };

  const handleEmbedCopy = () => {
    navigator.clipboard.writeText(iFrameCode);
    setEmbedCopied(true);
    setLinkCopied(false);
    message.success('Embed code copied to clipboard');
  };

  const handleSendInvites = async () => {
    if (error) {
      return;
    }
    try {
      shareProc(
        {
          pathParams: { id: procGroupId },
          body: { emails, sharing: true },
        },
        {
          onSuccess() {
            showNotification({
              type: 'success',
              message: 'Proc shared',
              description: `Link sent to the specified email${
                emails.length > 1 ? 's.' : '.'
              }`,
            });
            onCancel();
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'proc_share_modal_on_error_share_proc',
        action: 'share_proc',
      });
    }
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setError(false);
    }
  }, [open]);

  return (
    <Modal
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
            Share proc
          </Title>
        </Col>
      }
      forceRender
      width={500}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <Form
        name="inviteMemberForm"
        form={form}
        onFinish={handleSendInvites}
        layout="vertical"
      >
        <Row>
          <Form.Item
            name="emails"
            label="Emails"
            tooltip="Separate multiple emails with commas, semicolons, spaces or just hit enter key."
            rules={[{ required: true, message: 'Please enter email(s)' }]}
            style={{
              marginTop: 20,
              width: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <Select
              ref={selectRef}
              mode="tags"
              suffixIcon={null}
              style={{ width: '100%' }}
              open={false}
              status={error ? 'error' : undefined}
              tokenSeparators={[',', ';', ' ']}
              onChange={(value) => validateEmails(value)}
            />
          </Form.Item>
          <Button
            type="primary"
            loading={shareProcLoading}
            htmlType="submit"
            style={{ marginTop: 26 }}
          >
            Send link
          </Button>
        </Row>
        {error && (
          <div style={{ marginTop: -20, fontSize: 12, color: '#ff7875' }}>
            One or more emails you entered are not valid.
          </div>
        )}
      </Form>
      <label>Share via link</label>
      <Row>
        <Input
          value={
            import.meta.env.VITE_BASE_URL + `/procs/${procGroupId}/preview`
          }
          style={{ marginBottom: 30 }}
        />
        <Button
          type="primary"
          disabled={linkCopied}
          onClick={handleLinkCopy}
          style={{ marginTop: -30 }}
        >
          Copy link
        </Button>
      </Row>
      <label>Embed</label>
      <Row>
        <Input.TextArea
          value={iFrameCode}
          autoSize
          style={{ marginBottom: 30 }}
        />
        <Button
          type="primary"
          disabled={embedCopied}
          onClick={handleEmbedCopy}
          style={{ marginTop: -30 }}
        >
          Copy embed code
        </Button>
      </Row>
    </Modal>
  );
};
